/**
 * ProgressStrip — compact inline heartbeat docked in the chat input
 * row (alongside the upload + send buttons), NOT as a separate row.
 *
 * Renders as a 22px chip-style element:
 *
 *     [ ⏳ form_data · Validating join keys… · 7/11 ▾ ]
 *
 * Click to toggle the ProgressSheet which expands over the transcript.
 * Returns null when there's no progress to show, so the input row stays
 * clean and the upload + send buttons keep their original layout.
 *
 * Five visual states (consistent with the sheet + node badges):
 *     idle         → hidden (no active run)
 *     running      → primary tint, spinning glyph, activity follows task
 *     needs_input  → warning tint
 *     failed       → error tint
 *     queued       → neutral grey, "N tables queued"
 *     done         → success tint, terminal "Run complete N/N"
 */
import React from 'react';
import {
  Box,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandIcon,
  ExpandMore as CollapseIcon,
  HourglassEmpty as RunningIcon,
  CheckCircle as DoneIcon,
  ErrorOutline as FailedIcon,
  WarningAmber as NeedsInputIcon,
} from '@mui/icons-material';
import {
  currentRunningTask,
  overallProgress,
} from '../../../utils/progressReducer';
import type { ProgressState } from '../../../types/progress';

export interface ProgressStripProps {
  progress: ProgressState;
  expanded: boolean;
  onToggle: () => void;
}

function pickDisplayState(progress: ProgressState):
  | {
      kind: 'running' | 'needs_input' | 'failed' | 'done' | 'queued';
      phaseLabel: string;
      taskLabel: string;
      activity?: string | null;
      reason?: string | null;
    }
  | null {
  const running = currentRunningTask(progress);
  if (running && running.task.state === 'running') {
    return {
      kind: 'running',
      phaseLabel: running.phase.label,
      taskLabel: running.task.label,
      activity: running.task.activity,
    };
  }
  for (const phase of progress.phases) {
    const task = phase.tasks.find((t) => t.state === 'needs_input');
    if (task) {
      return {
        kind: 'needs_input',
        phaseLabel: phase.label,
        taskLabel: task.label,
        reason: task.reason,
      };
    }
  }
  for (const phase of progress.phases) {
    const task = phase.tasks.find((t) => t.state === 'failed');
    if (task) {
      return {
        kind: 'failed',
        phaseLabel: phase.label,
        taskLabel: task.label,
        reason: task.reason,
      };
    }
  }
  if (
    progress.phases.length > 0 &&
    progress.phases.every((p) => p.state === 'done')
  ) {
    return { kind: 'done', phaseLabel: 'Run complete', taskLabel: '' };
  }
  // Queued — phases seeded but no task started yet. Iterative early
  // exploration + engine Phase 1 ctx setup both hit this.
  for (const phase of progress.phases) {
    if (phase.tasks.length > 0) {
      const queuedCount = phase.tasks.filter((t) => t.state === 'queued').length;
      return {
        kind: 'queued',
        phaseLabel: phase.label,
        taskLabel: '',
        activity: `${queuedCount} ${queuedCount === 1 ? 'table' : 'tables'} queued`,
      };
    }
  }
  return null;
}

const ProgressStrip: React.FC<ProgressStripProps> = ({ progress, expanded, onToggle }) => {
  const theme = useTheme();
  const display = pickDisplayState(progress);
  if (!display) return null;

  const { pct, done, total } = overallProgress(progress);

  const palette = (() => {
    switch (display.kind) {
      case 'needs_input':
        return { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.dark, Icon: NeedsInputIcon };
      case 'failed':
        return { bg: alpha(theme.palette.error.main, 0.14), fg: theme.palette.error.dark, Icon: FailedIcon };
      case 'done':
        return { bg: alpha(theme.palette.success.main, 0.14), fg: theme.palette.success.dark, Icon: DoneIcon };
      case 'queued':
        return { bg: alpha(theme.palette.text.primary, 0.06), fg: theme.palette.text.secondary, Icon: RunningIcon };
      default:
        return { bg: alpha(theme.palette.primary.main, 0.12), fg: theme.palette.primary.dark, Icon: RunningIcon };
    }
  })();
  const { Icon } = palette;

  // Compact label: prefer task name + activity, fall back to phase name.
  const primary = display.taskLabel || display.phaseLabel;
  const secondary = display.activity || display.reason || '';

  const trailing =
    total > 0
      ? display.kind === 'done'
        ? `${total}/${total}`
        : `${done}/${total}`
      : '';

  const ToggleIcon = expanded ? CollapseIcon : ExpandIcon;
  const tooltipText = [display.phaseLabel, display.taskLabel, display.activity, display.reason]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={tooltipText || (expanded ? 'Hide progress' : 'Show progress details')} placement="top" arrow>
      <Box
        role="button"
        aria-label={expanded ? 'Hide progress details' : 'Show progress details'}
        aria-live="polite"
        onClick={onToggle}
        sx={{
          flex: 1,
          minWidth: 0,
          height: 22,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          borderRadius: 999,
          bgcolor: palette.bg,
          color: palette.fg,
          border: `1px solid ${alpha(palette.fg, 0.18)}`,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: alpha(palette.fg, 0.16) },
        }}
      >
        <Icon
          sx={{
            fontSize: 13,
            flexShrink: 0,
            color: palette.fg,
            animation:
              display.kind === 'running'
                ? 'progress-strip-spin 1.4s linear infinite'
                : 'none',
            '@keyframes progress-strip-spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 10.5,
            fontWeight: 600,
            color: palette.fg,
            lineHeight: 1,
          }}
        >
          {primary}
          {secondary && (
            <Box
              component="span"
              sx={{ fontWeight: 400, opacity: 0.85, ml: 0.5 }}
            >
              · {secondary}
            </Box>
          )}
        </Typography>
        {trailing && (
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              fontSize: 10,
              fontVariantNumeric: 'tabular-nums',
              color: palette.fg,
              opacity: 0.85,
              lineHeight: 1,
            }}
          >
            {trailing}
            {display.kind === 'running' && total > 0 ? ` · ${pct}%` : ''}
          </Typography>
        )}
        <ToggleIcon sx={{ fontSize: 14, flexShrink: 0, color: palette.fg }} />
      </Box>
    </Tooltip>
  );
};

export default ProgressStrip;
